import {
  NOT_SHOWN_REASONS,
  notShownReasonSchema,
  type ProductDetail,
  type ProductStatus,
  type ProductSummary,
  type UpdateProductRequest,
  PRODUCT_ORIGINS,
  PRODUCT_SOURCE_STATES,
  PRODUCT_STATUSES,
  productOriginSchema,
  productSourceStateSchema,
  productStatusSchema,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  buttonClass,
  Card,
  CategoryBrowser,
  Cell,
  hasGroupedProducts,
  LoadMore,
  Loading,
  PageHeader,
  PencilIcon,
  SelectField,
  Table,
  TextField,
  useListFilters,
} from '@avrahami/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import {
  keys,
  NO_PRODUCT_FILTERS,
  productGroupsQuery,
  productListQuery,
  tenantApi,
  type ProductFilters,
} from '../api/tenant';
import { ProductStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { notShownReasons, refusalOf } from '../lib/display';
import { productRowDraft, productRowRequest, type ProductRowDraft } from '../lib/drafts';
import { errorMessage, mergeErrors, NO_ERRORS, type FieldErrors } from '../lib/forms';
import { formatMoney, formatProfit, profitOf, shekelsToMinor } from '../lib/money';

/**
 * The list may be opened with filters in the address (`/products?status=DRAFT`, as the pull
 * screen links to it, or `?notShown=NO_PRICE`, as the dashboard does). A new address starts the
 * list again with those filters.
 *
 * The products are shown under their categories: first the categories, a category opens to its
 * sub-categories, and only a sub-category shows products. A search shows its results at once.
 * The filters and the open categories are remembered, so coming back from a product shows the
 * list as it was left.
 */
export function ProductsPage() {
  const { status = '', sourceState = '', notShown = '' } = useSearch({ from: '/app/products' });
  return (
    <ProductList
      key={`${status}|${sourceState}|${notShown}`}
      name={`products:${status}|${sourceState}|${notShown}`}
      initial={{ ...NO_PRODUCT_FILTERS, status, sourceState, notShown }}
    />
  );
}

function ProductList({ name, initial }: { name: string; initial: ProductFilters }) {
  const navigate = useNavigate();
  const canWrite = useCan('store.products.write');
  const canPull = useCan('store.sync.run');
  const { draft, filters, change: set, apply, reset } = useListFilters(name, initial);
  const searching = filters.q.trim() !== '';
  const groups = useQuery({ ...productGroupsQuery(filters), enabled: !searching });

  const onFilter = (event: SyntheticEvent) => {
    event.preventDefault();
    apply();
  };

  return (
    <>
      <PageHeader
        title={he.products.title}
        actions={
          <div className="flex flex-wrap gap-2">
            {canPull ? (
              <Link
                to="/products/pull"
                className={buttonClass('secondary')}
                data-testid="product-pull"
              >
                {he.products.pull}
              </Link>
            ) : null}
            {canWrite ? (
              <Link to="/products/new" className={buttonClass()} data-testid="product-create">
                {he.products.create}
              </Link>
            ) : null}
          </div>
        }
      />
      <Card>
        <form onSubmit={onFilter} className="mb-4 flex flex-wrap items-end gap-3">
          <TextField
            label={he.products.search}
            name="q"
            type="search"
            className="w-72"
            maxLength={100}
            value={draft.q}
            onChange={(event) => {
              set({ q: event.target.value });
            }}
          />
          <SelectField
            label={he.products.status}
            name="status"
            value={draft.status}
            onChange={(event) => {
              const value = event.target.value;
              set({ status: value === '' ? '' : productStatusSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {PRODUCT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {he.productStatus[status]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={he.products.origin}
            name="origin"
            value={draft.origin}
            onChange={(event) => {
              const value = event.target.value;
              set({ origin: value === '' ? '' : productOriginSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {PRODUCT_ORIGINS.map((origin) => (
              <option key={origin} value={origin}>
                {he.productOrigin[origin]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={he.products.sourceState}
            name="sourceState"
            value={draft.sourceState}
            onChange={(event) => {
              const value = event.target.value;
              set({ sourceState: value === '' ? '' : productSourceStateSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {PRODUCT_SOURCE_STATES.map((state) => (
              <option key={state} value={state}>
                {he.productSourceState[state]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={he.products.notShown}
            name="notShown"
            value={draft.notShown}
            onChange={(event) => {
              const value = event.target.value;
              set({ notShown: value === '' ? '' : notShownReasonSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {NOT_SHOWN_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {he.notShownReason[reason]}
              </option>
            ))}
          </SelectField>
          <Button type="submit" tone="secondary">
            {he.common.search}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              reset(NO_PRODUCT_FILTERS);
              // Filters that came with the address leave it too.
              void navigate({ to: '/products', search: {}, replace: true });
            }}
          >
            {he.common.clear}
          </Button>
        </form>
        {searching ? (
          <ProductRows filters={filters} />
        ) : (
          <>
            {groups.isPending ? <Loading /> : null}
            {groups.isError ? <Alert>{errorMessage(groups.error)}</Alert> : null}
            {groups.data === undefined ? null : hasGroupedProducts(groups.data) ? (
              <CategoryBrowser
                name={name}
                groups={groups.data}
                renderProducts={(categoryId) => (
                  <ProductRows filters={{ ...filters, categoryId }} />
                )}
              />
            ) : (
              <p className="text-sm text-slate-500" data-testid="products-empty">
                {he.products.empty}
              </p>
            )}
          </>
        )}
      </Card>
    </>
  );
}

/** The products of one category, or the results of a search: a table that loads more. */
function ProductRows({ filters }: { filters: ProductFilters }) {
  const canWrite = useCan('store.products.write');
  const products = useInfiniteQuery(productListQuery(filters));
  const items = products.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <>
      {products.isPending ? <Loading /> : null}
      {products.isError ? <Alert>{errorMessage(products.error)}</Alert> : null}
      {products.isSuccess && items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="products-empty">
          {he.products.empty}
        </p>
      ) : null}
      {items.length > 0 ? (
        <Table
          head={[
            he.products.name,
            he.products.sku,
            he.products.status,
            he.products.origin,
            he.products.cost,
            he.products.price,
            he.products.profit,
            he.common.actions,
          ]}
        >
          {items.map((product) => (
            <ProductRow key={product.id} product={product} canWrite={canWrite} />
          ))}
        </Table>
      ) : null}
      <LoadMore
        hasMore={products.hasNextPage}
        busy={products.isFetchingNextPage}
        onMore={() => {
          void products.fetchNextPage();
        }}
      />
    </>
  );
}

const cellInput =
  'w-full rounded-md border px-2 py-1 text-sm shadow-sm focus:outline-2 focus:outline-brand-600 disabled:bg-slate-50';

function inputClass(error: string | undefined): string {
  return `${cellInput} ${error === undefined ? 'border-slate-300' : 'border-red-500'}`;
}

/** The row as last saved: the product the list read, or a newer answer of a save in this row. */
interface SavedRow {
  version: number;
  status: ProductStatus;
  compareAtMinor: number | null;
  values: ProductRowDraft;
}

function savedFromSummary(product: ProductSummary): SavedRow {
  return {
    version: product.version,
    status: product.status,
    compareAtMinor: product.compareAtMinor,
    values: productRowDraft(product),
  };
}

function savedFromDetail(product: ProductDetail): SavedRow {
  return {
    version: product.version,
    status: product.status,
    compareAtMinor: product.price.compareAtMinor,
    values: productRowDraft({
      title: product.content.title,
      sku: product.facts.sku,
      costMinor: product.price.costMinor,
      amountMinor: product.price.amountMinor,
    }),
  };
}

const ROW_FIELDS = ['content.title', 'facts.sku', 'price.amountMinor', 'price.costMinor'];

/** A field's message under its input. */
function FieldError({ error }: { error: string | undefined }) {
  return error === undefined ? null : <div className="mt-1 text-xs text-red-600">{error}</div>;
}

/**
 * One product, edited in place. The status is a list and is saved as soon as it is chosen; the
 * texts (the title, the SKU and cost of the store's own product, the sale price) are saved with
 * "עדכון", or Enter. The SKU and the cost of a catalog product come from the catalog and are
 * only shown.
 */
function ProductRow({ product, canWrite }: { product: ProductSummary; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const locked = product.origin === 'MASTER_CATALOG';
  // What a save in this row answered, until the list reads that version (or a newer one).
  const [answer, setAnswer] = useState<SavedRow | null>(null);
  const saved =
    answer !== null && answer.version > product.version ? answer : savedFromSummary(product);
  // Only what was typed: a field left alone always shows what is saved.
  const [typed, setTyped] = useState<Partial<ProductRowDraft>>({});
  const values: ProductRowDraft = { ...saved.values, ...typed };
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);

  const save = useMutation({
    mutationFn: (body: UpdateProductRequest) => tenantApi.updateProduct(product.id, body),
    onSuccess: async (updated, body) => {
      setAnswer(savedFromDetail(updated));
      // A status chosen while texts were typed leaves the texts for their own "עדכון".
      if (body.status === undefined) setTyped({});
      queryClient.setQueryData(keys.product(product.id), updated);
      await queryClient.invalidateQueries({ queryKey: keys.products() });
      await queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });

  const dirty = (Object.keys(typed) as (keyof ProductRowDraft)[]).some(
    (field) => typed[field] !== saved.values[field],
  );
  const type = (field: keyof ProductRowDraft, value: string) => {
    setTyped((current) => ({ ...current, [field]: value }));
  };
  const update = () => {
    if (!dirty || save.isPending) return;
    const parsed = productRowRequest(values, saved.values, {
      version: saved.version,
      locked,
      compareAtMinor: saved.compareAtMinor,
    });
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };
  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    update();
  };
  const reload = () => {
    save.reset();
    setAnswer(null);
    setTyped({});
    setLocalErrors(NO_ERRORS);
    void queryClient.invalidateQueries({ queryKey: keys.products() });
  };

  const errors = mergeErrors(localErrors, save.error);
  const onField = ROW_FIELDS.some((path) => errors[path] !== undefined);
  const refusal = refusalOf(save.error);
  // Anything not shown under a field: why the product cannot be shown, or the API's answer.
  const message =
    refusal ??
    (save.error !== null && !onField ? errorMessage(save.error) : null) ??
    (Object.keys(localErrors).length > 0 && !onField ? he.common.invalidField : null);
  const stale = save.error !== null && refusal === null && !onField;
  const status: ProductStatus =
    save.isPending && save.variables.status !== undefined ? save.variables.status : saved.status;
  const priceMinor = shekelsToMinor(values.price) ?? 0;
  const profit = profitOf(shekelsToMinor(values.cost), priceMinor);
  const name = product.title ?? product.name;

  return (
    <tr data-testid="product-row" data-sku={product.sku}>
      <Cell className="min-w-40">
        {canWrite ? (
          <input
            className={inputClass(errors['content.title'])}
            aria-invalid={errors['content.title'] !== undefined}
            aria-label={`${he.products.name}: ${name}`}
            data-testid="product-title-input"
            maxLength={200}
            value={values.title}
            placeholder={product.name}
            onChange={(event) => {
              type('title', event.target.value);
            }}
            onKeyDown={onEnter}
          />
        ) : (
          <span className="font-medium text-slate-900">{name}</span>
        )}
        <FieldError error={errors['content.title']} />
        {product.sourceState === 'SOURCE_DISCONTINUED' ? (
          <div className="text-xs text-amber-700">{he.dashboard.sourceDiscontinued}</div>
        ) : null}
      </Cell>
      <Cell className="ltr">
        {canWrite && !locked ? (
          <input
            className={inputClass(errors['facts.sku'])}
            aria-invalid={errors['facts.sku'] !== undefined}
            aria-label={`${he.products.sku}: ${name}`}
            data-testid="product-sku-input"
            maxLength={50}
            value={values.sku}
            onChange={(event) => {
              type('sku', event.target.value);
            }}
            onKeyDown={onEnter}
          />
        ) : (
          <span title={locked ? he.products.lockedFromCatalog : undefined}>
            {/* A store SKU ("<source>/<customer number>") may wrap after its slash. */}
            {product.sku.split('/').map((part, index) => (
              <span key={index}>
                {index > 0 ? '/' : null}
                {index > 0 ? <wbr /> : null}
                {part}
              </span>
            ))}
          </span>
        )}
        <FieldError error={errors['facts.sku']} />
      </Cell>
      <Cell className="min-w-36">
        {canWrite ? (
          <select
            className={inputClass(undefined)}
            aria-label={`${he.products.status}: ${name}`}
            data-testid="product-status-select"
            value={status}
            disabled={save.isPending}
            onChange={(event) => {
              const next = productStatusSchema.parse(event.target.value);
              if (next !== saved.status) save.mutate({ version: saved.version, status: next });
            }}
          >
            {PRODUCT_STATUSES.map((option) => (
              <option key={option} value={option}>
                {he.productStatus[option]}
              </option>
            ))}
          </select>
        ) : (
          <ProductStatusBadge
            status={product.status}
            hiddenFromShoppers={product.hiddenFromShoppers}
          />
        )}
        {/* Why shoppers do not see it: never left for the store to guess. */}
        {product.status === 'ACTIVE' && product.hiddenFromShoppers !== null ? (
          <div
            className="mt-1 text-xs text-amber-700"
            data-testid="product-hidden-from-shoppers"
            data-reason={product.hiddenFromShoppers}
          >
            {he.shopperHidden[product.hiddenFromShoppers]}
          </div>
        ) : null}
        {notShownReasons(product).map((reason) => (
          <div
            key={reason}
            className="mt-1 text-xs text-amber-700"
            data-testid="product-not-shown"
            data-reason={reason}
          >
            {he.notShownReason[reason]}
          </div>
        ))}
      </Cell>
      <Cell>{he.productOrigin[product.origin]}</Cell>
      <Cell className="min-w-24">
        {canWrite && !locked ? (
          <input
            className={inputClass(errors['price.costMinor'])}
            aria-invalid={errors['price.costMinor'] !== undefined}
            aria-label={`${he.products.cost}: ${name}`}
            data-testid="product-cost-input"
            inputMode="decimal"
            dir="ltr"
            autoComplete="off"
            value={values.cost}
            onChange={(event) => {
              type('cost', event.target.value);
            }}
            onKeyDown={onEnter}
          />
        ) : (
          <span data-testid="product-cost" title={locked ? he.products.costLockedHint : undefined}>
            {product.costMinor === null ? he.common.none : formatMoney(product.costMinor)}
          </span>
        )}
        <FieldError error={errors['price.costMinor']} />
      </Cell>
      <Cell className="min-w-24">
        {canWrite ? (
          <input
            className={inputClass(errors['price.amountMinor'])}
            aria-invalid={errors['price.amountMinor'] !== undefined}
            aria-label={`${he.products.price}: ${name}`}
            data-testid="product-price-input"
            inputMode="decimal"
            dir="ltr"
            autoComplete="off"
            value={values.price}
            onChange={(event) => {
              type('price', event.target.value);
            }}
            onKeyDown={onEnter}
          />
        ) : (
          <span data-testid="product-price">{formatMoney(product.amountMinor ?? 0)}</span>
        )}
        <FieldError error={errors['price.amountMinor']} />
        {/* A product shoppers do not see at all says so under its status, not here. */}
        {priceMinor === 0 && product.hiddenFromShoppers === null ? (
          <div className="mt-1 text-xs text-slate-500" data-testid="product-zero-price">
            {he.products.zeroPriceHint}
          </div>
        ) : null}
      </Cell>
      <Cell>
        <span
          data-testid="product-profit"
          className={profit !== null && profit.amountMinor < 0 ? 'text-red-600' : ''}
        >
          {profit === null ? he.common.none : formatProfit(profit)}
        </span>
      </Cell>
      <Cell>
        <div className="flex flex-col items-start gap-2">
          {canWrite ? (
            <Button
              tone={dirty ? 'primary' : 'secondary'}
              disabled={!dirty}
              busy={save.isPending && save.variables.status === undefined}
              data-testid="product-update"
              onClick={update}
            >
              {he.products.update}
            </Button>
          ) : null}
          <Link
            to="/products/$productId"
            params={{ productId: product.id }}
            aria-label={`${he.products.edit}: ${name}`}
            title={he.products.edit}
            data-testid="product-edit"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-brand-700 ring-1 ring-slate-200 hover:bg-brand-50"
          >
            <PencilIcon />
          </Link>
        </div>
        {save.isSuccess && !dirty ? (
          <div className="mt-1 text-xs text-green-700" role="status" data-testid="product-updated">
            {he.products.updated}
          </div>
        ) : null}
        {message === null ? null : (
          <div
            className="mt-1 max-w-56 text-xs text-red-600"
            role="alert"
            data-testid="product-row-error"
          >
            {message}
            {stale ? (
              <button type="button" className="ms-1 underline" onClick={reload}>
                {he.products.reloadRow}
              </button>
            ) : null}
          </div>
        )}
      </Cell>
    </tr>
  );
}
